<template>
<div v-if="inputValue && spans">

  <div class="my-8">
    <h5>延長パターン名</h5>
    <v-text-field
      v-model="inputValue.patternTitle"
      variant="outlined"
      hide-details
      density="compact"
    ></v-text-field>
  </div>

  <!-- 固定時間枠（幼稚園） -->
  <div class="menu-header">固定時間枠（幼稚園）</div>

  <div class="my-8">
    <div>
      <h5>開始時刻</h5>
      <time-input
        :hour="inputValue.startingHour"
        @hour="inputValue.startingHour = Number($event)"
        :minute="inputValue.startingMinute"
        @minute="inputValue.startingMinute = Number($event)"
      ></time-input>
    </div>

    <div class="my-8">
      <billing-input
        :billingMethod="inputValue.earlyHoursBillingFee.billingMethod"
        @billingMethod="inputValue.earlyHoursBillingFee.billingMethod = $event"
        :hourly="inputValue.earlyHoursBillingFee.hourly"
        @hourly="inputValue.earlyHoursBillingFee.hourly = $event"
        :monthly="inputValue.earlyHoursBillingFee.monthly"
        @monthly="inputValue.earlyHoursBillingFee.monthly = $event"
      ></billing-input>
    </div>
  </div>

  <!-- 時間枠 -->
  <div v-for="(item, i) in spans.spans" :key="i">
    <div class="menu-header">時間枠{{i+1}}</div>

    <div class="my-8">
      <div>
        <h5>開始時刻</h5>
        <time-input
          :hour="item.startingHour"
          @hour="item.startingHour = Number($event)"
          :minute="item.startingMinute"
          @minute="item.startingMinute = Number($event)"
        ></time-input>
      </div>

      <div class="mt-4">
        <h5>終了時刻</h5>
        <time-input
          :hour="item.endingHour"
          @hour="item.endingHour = Number($event)"
          :minute="item.endingMinute"
          @minute="item.endingMinute = Number($event)"
        ></time-input>
      </div>

      <div class="my-8">
        <billing-input
          :billingMethod="item.billingMethod"
          @billingMethod="item.billingMethod = $event"
          :hourly="item.hourly"
          @hourly="item.hourly = $event"
          :monthly="item.monthly"
          @monthly="item.monthly = $event"
        ></billing-input>
      </div>
    </div>

  </div>

  <div class="d-flex">
    <v-btn
      @click="addSpanRow"
      color="primary"
      variant="outlined"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>
  </div>

  <div class="my-12 d-flex justify-center">
    <v-btn
      @click="updatePattern"
      v-if="isUpdatable"
      :loading="isLoading"
      color="primary"
      variant="flat"
      large
    >保存</v-btn>
    <v-btn
      @click="createPattern"
      v-else
      :loading="isLoading"
      color="primary"
      variant="flat"
      large
    >作成</v-btn>
  </div>

</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { CreateSpansInput, CreateSpotKindergartenInput, nurseryHoursModule } from '@/store/dataModules/nurseryHoursModule'
import LocalDataService from '@/service/LocalDataService';
import Swal from 'sweetalert2';
import TimeInput from '@/components/organisms/nursery-hours/TimeInput.vue'
import BillingInput from '@/components/organisms/nursery-hours/BillingInput.vue';

export default defineComponent({
  name: 'SpotKindergartenPatternContainer',

  components: {
    TimeInput,
    BillingInput,
  },

  mounted() {
    if (!this.isUpdatable) {
      this.initValues();
      this.initSpans();
      this.addSpanRow();
    }
  },

  data() {
    return {
      billingMethodItems: [
        { text: '時間あたり', value: 'hourly', },
        { text: '月あたり', value: 'monthly', },
      ],
      isLoading: false,
    }
  },

  computed: {
    inputValue: {
      get(): CreateSpotKindergartenInput | null {
        return nurseryHoursModule.spotKindergartenPatternInput;
      },
      set(value: CreateSpotKindergartenInput) {
        nurseryHoursModule.setSpotKindergartenPatternInput(value);
      }
    },
    isUpdatable: function(): boolean {
      return (this.$route.query && this.$route.query.category != null); 
    },
    spans: {
      get(): CreateSpansInput | null {
        return nurseryHoursModule.spansInput;
      },
      set(value: CreateSpansInput) {
        nurseryHoursModule.setSpansInput(value);
      }
    },
  },

  methods: {
    initValues() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        this.inputValue = {
          nurseryId: nurseryId,
          patternTitle: '延長パターン',
          startingHour: 7,
          startingMinute: 0,
          earlyHoursBillingFee: {
            billingMethod: 'hourly',
            hourly: {
              billingMinute: 30,
              billingAmount: 100,
              billingUpperLimitAmount: undefined,
            },
            monthly: {
              billingAmount: 100,
            }
          },
        };
      }
    },
    initSpans() {
      this.spans = {
        patternId: '0',
        spans: [],
        spanCategory: 'normal',
      }
    },
    async createPattern() {
      this.isLoading = true;
      const response = await nurseryHoursModule.createSpotKindergartenPattern()
      .catch((e) => {
        alert("パターンの設定に失敗しました");
      });
      if (response) {
        if (response.patternId) {
          await this.createSpans(response.patternId);
        }
        Swal.fire({
          text: '作成しました',
        });
        this.$router.push({ path: '/nursery-hours' });
      }
      this.isLoading = false;
    },
    async updatePattern() {
      this.isLoading = true;
      const patternId = this.$route.query?.patternId;
      if (patternId) {
        const response = await nurseryHoursModule.updateSpotKindergartenPattern({
          patternId: String(patternId),
        })
        .catch((e) => {
          alert("パターンの設定に失敗しました");
        });
        if (response) {
          if (response.patternId) {
            await this.updateSpans(response.patternId);
          }
          Swal.fire({
            title: '保存しました',
          });
        this.$router.push({ path: '/nursery-hours' });
        }
      }
      this.isLoading = false;
    },
    async createSpans(patternId: string) {
      if (patternId && nurseryHoursModule.spansInput) {
        nurseryHoursModule.spansInput.patternId = patternId;
        await nurseryHoursModule.saveSpans()
        .catch((e) => {
          alert("不正な値が入力されています。時間枠の設定を見直して再度行ってください");
        });
      }
    },
    async updateSpans(patternId: string) {
      if (patternId && nurseryHoursModule.spansInput) {
        nurseryHoursModule.spansInput.patternId = patternId;
        await nurseryHoursModule.updateSpans()
        .catch((e) => {
          alert("不正な値が入力されています。時間枠の設定を見直して再度行ってください");
        });
      }
    },
    addSpanRow() {
      this.spans?.spans.push({
        startingHour: 18,
        startingMinute: 0,
        endingHour: 18,
        endingMinute: 59,
        billingMethod: 'hourly',
        hourly: {
          billingMinute: 30,
          billingAmount: 100,
          billingUpperLimitAmount: 1000,
        },
        monthly: {
          billingAmount: 100,
        }
      });
    },
  },
})
</script>